// /public/proposals/:token — página PÚBLICA read-only da proposta (Story 11.7 AC2).
// O lead/cliente abre este link SEM login. Fora do layout autenticado (App.tsx).
// Mobile-first, design system. Distingue 404 (token inválido) de 410 (expirada).

import axios from 'axios';
import { FileText } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { formatBR, type ProposalPublicView } from '@control-center/shared';

import { usePublicProposal } from '../../hooks/use-crm-proposals';
import { PROPOSAL_STATUS_LABELS, formatProposalMoney } from '../../lib/proposal-helpers';

export function PublicProposalPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, isError, error } = usePublicProposal(token);

  if (isLoading) {
    return <Centered>{<LoadingCard />}</Centered>;
  }

  if (isError || !data) {
    const expired = axios.isAxiosError(error) && error.response?.status === 410;
    return (
      <Centered>
        <StatusCard
          title={expired ? 'Esta proposta expirou' : 'Proposta não encontrada'}
          message={
            expired
              ? 'O prazo de validade desta proposta passou. Entre em contato com seu consultor para uma nova.'
              : 'O link pode estar incorreto ou a proposta não está mais disponível.'
          }
        />
      </Centered>
    );
  }

  return (
    <Centered>
      <ProposalCard proposal={data} />
    </Centered>
  );
}

function ProposalCard({ proposal }: { proposal: ProposalPublicView }): JSX.Element {
  const rows: { label: string; value: string }[] = [
    { label: 'Produto', value: proposal.saas_product.name },
    { label: 'Plano', value: proposal.plan.name },
    { label: 'Ciclo de cobrança', value: proposal.billing_cycle },
    { label: 'Mensal', value: formatProposalMoney(proposal.monthly_value) },
    { label: 'Anual', value: formatProposalMoney(proposal.annual_value) },
    { label: 'Setup', value: formatProposalMoney(proposal.setup_value) },
    { label: 'Desconto', value: formatProposalMoney(proposal.discount_amount) },
    { label: 'Válida até', value: formatBR(proposal.valid_until) },
  ];

  return (
    <div className="w-full max-w-[520px] rounded-[16px] border border-line bg-panel p-6 sm:p-8">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-panel-2 text-ink-2">
          <FileText size={20} strokeWidth={1.8} />
        </span>
        <div>
          <h1 className="m-0 text-[20px] font-bold tracking-[-0.01em] text-ink">
            Proposta {proposal.proposal_number}
          </h1>
          <p className="m-0 text-[12px] text-muted">{PROPOSAL_STATUS_LABELS[proposal.status]}</p>
        </div>
      </div>

      <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {rows.map((r) => (
          <div key={r.label} className="flex flex-col gap-[2px]">
            <dt className="text-[11px] font-medium uppercase tracking-wider text-muted-3">
              {r.label}
            </dt>
            <dd className="m-0 text-[14px] text-ink">{r.value}</dd>
          </div>
        ))}
      </dl>

      {proposal.observations && (
        <div className="mt-5 border-t border-line pt-4">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-3">
            Observações
          </p>
          <p className="mt-1 whitespace-pre-wrap text-[13px] text-ink-2">{proposal.observations}</p>
        </div>
      )}

      <p className="mt-6 text-[12px] leading-[1.5] text-muted">
        Esta é uma visualização da sua proposta. Para aceitar, recusar ou tirar dúvidas, fale com
        seu consultor.
      </p>
    </div>
  );
}

function StatusCard({ title, message }: { title: string; message: string }): JSX.Element {
  return (
    <div className="w-full max-w-[480px] rounded-[16px] border border-line bg-panel p-8 text-center">
      <h1 className="m-0 text-[20px] font-bold tracking-[-0.01em] text-ink">{title}</h1>
      <p className="mt-2 text-[13px] text-muted">{message}</p>
    </div>
  );
}

function LoadingCard(): JSX.Element {
  return (
    <div className="w-full max-w-[520px] rounded-[16px] border border-line bg-panel p-8">
      <div className="h-6 w-48 animate-pulse rounded bg-panel-2" />
      <div className="mt-6 grid grid-cols-2 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse rounded bg-panel-2" />
        ))}
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-4 py-10">
      {children}
    </div>
  );
}
